import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Tab,
  Tabs,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
} from '@mui/material';

import {
  PersonAdd as PersonAddIcon,
  GroupAdd as GroupAddIcon,
} from '@mui/icons-material';

import TeamMembers from './TeamMembers';
import { Teams } from './Teams';
import api from '../../services/api';
import teamService from '../../services/teamService';
import { useAccount } from '../../contexts/AccountContext';
import { useLoader } from '../../contexts/LoaderContext';
import { useModal } from '../../contexts/ModalContext';
import { useAuth } from '../../contexts/AuthContext';
import UserLimitAlertModal from './UserLimitAlertModal';
import TeamLimitAlertModal from './TeamLimitAlertModal';
import AddUserSlotModal from './AddUserSlotModal';
import QueryForm from '../../components/common/QueryForm';
import { validateUser } from '../../utils/validationRules';

const UsersAndTeams = () => {
  const { accountId, currentPlan, isNewTeamAllowed } = useAccount();
  const { user } = useAuth();
  const [tabValue, setTabValue] = useState(0);
  const [openUserDialog, setOpenUserDialog] = useState(false);
  const [users, setUsers] = useState([]);
  const [userRoles, setUserRoles] = useState([]);
  const [teams, setTeams] = useState([]);
  const [newUserAllowed, setNewUserAllowed] = useState(false);
  const [newTeamAllowed, setNewTeamAllowed] = useState(false);
  const [showQueryForm, setShowQueryForm] = useState(false);
  const [newUser, setNewUser] = useState({ 
    name: '', 
    email: '', 
    designation: '',
    mobile: '',
    role: '' 
  });
  const { showLoader, hideLoader } = useLoader();
  const { openSuccessModal, openErrorModal, handleHttpError, openConfirmModal } = useModal();
  const [fieldErrors, setFieldErrors] = useState({});
  const [showUserLimitModal, setShowUserLimitModal] = useState(false);
  const [showAddUserSlotModal, setShowAddUserSlotModal] = useState(false);
  const [showTeamLimitModal, setShowTeamLimitModal] = useState(false);
  const [maxAllowed, setMaxAllowed] = useState(0);
  const [whatsappSettings, setWhatsappSettings] = useState({
    is_whatsapp_connected: false,
    whatsapp_messaging_enabled: false,
  });

  // Show Teams tab only for admin users
  const showTeamsTab = user?.role === 'AD';

  useEffect(() => {
    if (accountId) {
      fetchUsersData();
      fetchTeamsData();
      fetchUserRoles();
      fetchWhatsAppSettings();
    }
  }, [accountId]);

  const fetchUsersData = async () => {
    try {
      showLoader();
      const response = await api.get(`account/users/${accountId}/`);
      setUsers(response.data);
    } catch (error) {
      handleHttpError(error);
    } finally {
      hideLoader();
    }
  };

  const fetchTeamsData = async () => {
    try {
      showLoader();
      const response = await teamService.getTeams(accountId);
      setTeams(response.data);
    } catch (error) {
      handleHttpError(error);
    } finally {
      hideLoader();
    }
  };

  const fetchUserRoles = async () => {
    try {
      showLoader();
      const response = await api.get(`account/user_roles/`);
      setUserRoles(response.data);
    } catch (error) {
      handleHttpError(error);
    } finally {
      hideLoader();
    }
  };

  const fetchWhatsAppSettings = async () => {
    try {
      const response = await api.get('/lms/integrations/whatsapp/settings/');
      setWhatsappSettings({
        is_whatsapp_connected: response.data.is_whatsapp_connected || false,
        whatsapp_messaging_enabled: response.data.whatsapp_messaging_enabled || false,
      });
    } catch (error) {
      console.error('Error fetching WhatsApp settings:', error);
      setWhatsappSettings({
        is_whatsapp_connected: false,
        whatsapp_messaging_enabled: false,
      });
    }
  };

  const refreshAllData = async () => {
    await fetchUsersData();
    await fetchTeamsData();
  }

  const handleAddUser = async () => {
    try {
      // Validate user data
      const validationErrors = validateUser(newUser, users);
      
      if (Object.keys(validationErrors).length > 0) {
        setFieldErrors(validationErrors);
        return;
      }

      showLoader();
      const response = await api.post(`account/users/${accountId}/`, newUser);
      openSuccessModal('Success', 'New team member added successfully');
      handleCloseUserDialog();
      await fetchUsersData();
    } catch (error) {
      if (error.response?.data) {
        const errorData = error.response.data;
        
        // Map backend field errors to form fields
        const fieldErrorMapping = {
          email: 'email',
          designation: 'designation',
          name: 'name',
          mobile: 'mobile',
          role: 'role'
        };

        const newFieldErrors = {};
        let hasFieldErrors = false;

        // Process each error from the backend
        Object.entries(errorData).forEach(([field, message]) => {
          const formField = fieldErrorMapping[field];
          if (formField) {
            hasFieldErrors = true;
            newFieldErrors[formField] = Array.isArray(message) ? message[0] : message;
          }
        });

        if (hasFieldErrors) {
          setFieldErrors(newFieldErrors);
        } else {
          openErrorModal(typeof errorData === 'string' ? errorData : 'An error occurred while creating the user');
        }
      } else {
        openErrorModal('An unexpected error occurred');
      }
    } finally {
      hideLoader();
    }
  };

  const confirmUserDelete = (userId) => {
    const member = users.find(u => u.id === userId);
    if (member.role === 'AD') {
      openErrorModal('Admin user cannot be deleted');
      return;
    }

    // For MG users, only allow deleting users from their own team
    if (user?.role === 'MG' && member.team_id !== user.team_id) {
      openErrorModal('You can only delete members from your own team');
      return;
    }

    openConfirmModal(
      'Delete User',  // title
      'Are you sure you want to delete this user?', // message
      async () => {   // onConfirm callback
        try {
          showLoader("Submitting...");
          await api.delete(`account/user/${accountId}/${userId}/`);
          openSuccessModal("Success", 'Team member removed successfully');
          fetchUsersData();
        } catch (error) {
          handleHttpError(error);
        } finally {
          hideLoader();
        }
      }
    );
  };

  const confirmTeamDelete = (teamId) => {
    const teamUsers = users.filter(user => user.team_id === teamId);
    let confirmMessage = 'Are you sure you want to delete this team?';
    if (teamUsers.length > 0) {
      confirmMessage = `This team has ${teamUsers.length} members. They will be unassigned from this team. Do you want to continue?`;
    }

    openConfirmModal(
      'Delete Team',  // title
      confirmMessage, // message
      async () => {   // onConfirm callback
        try {
          showLoader("Submitting...");
          await teamService.deleteTeam(accountId, teamId);
          openSuccessModal("Success", 'Team deleted successfully');
          fetchTeamsData();
        } catch (error) {
          handleHttpError(error);
        } finally {
          hideLoader();
        }
      }
    );
  };

  const handleEditUser = async (userId, userData) => {
    try {
      showLoader("Submitting...");
      
      // Check if mobile_action_required_mandatory or lead_number_masking_enabled was changed
      const originalUser = users.find(u => u.id === userId);
      const mobileSettingChanged = originalUser && 
        'mobile_action_required_mandatory' in userData &&
        originalUser.mobile_action_required_mandatory !== userData.mobile_action_required_mandatory;
      
      const maskingSettingChanged = originalUser &&
        'lead_number_masking_enabled' in userData &&
        originalUser.lead_number_masking_enabled !== userData.lead_number_masking_enabled;
      
      const response = await api.put(`account/user/${accountId}/${userId}/`, userData);
      if(response.status === 200) {
        // Fetch updated user data after successful edit
        await fetchUsersData();
        
        // Show appropriate message based on whether mobile settings were changed
        if (mobileSettingChanged || maskingSettingChanged) {
          openSuccessModal(
            'Success', 
            'User updated successfully. Please note: Mobile app settings have been changed. The user needs to logout and login again in the mobile app for changes to take effect.'
          );
        } else {
          openSuccessModal('Success', 'User updated successfully');
        }
        return true;
      } else {
        return false;
      }
    } catch (error) {
      if (error.response?.status === 400 && error.response?.data) {
        return error.response.data;
      } else {
        handleHttpError(error);
        return false;
      }
    } finally {
      hideLoader();
    }
  };

  const handleEditTeam = async (teamId, teamData) => {
    try {
      showLoader("Submitting...");
      await teamService.updateTeam(accountId, teamId, teamData);
      await fetchTeamsData();
      openSuccessModal('Success', 'Team updated successfully');
      return true; // Indicate success to close edit dialog
    } catch (error) {
      hideLoader();
      if (error.response?.status === 400) {
        // Return error data to keep edit dialog open
        return error.response.data || false;
      } else {
        handleHttpError(error);
        return false;
      }
    } finally {
      hideLoader();
    }
  };

  const handleCreateTeam = async (teamData) => {
    try {
      showLoader("Creating team...");
      await teamService.createTeam(accountId, teamData);
      await fetchTeamsData();
      openSuccessModal('Success', 'Team created successfully');
      return true;
    } catch (error) {
      if (error.response?.status === 400) {
        return error.response.data || false;
      } else {
        handleHttpError(error);
        return false;
      }
    } finally {
      hideLoader();
    }
  };

  const handleManageTeamMembers = async (teamId) => {
    try {
      showLoader("Loading team members...");
      const response = await teamService.getTeamMembers(accountId, teamId);
      // This function will be used by the TeamDetail component
      return response.data;
    } catch (error) {
      handleHttpError(error);
      return null;
    } finally {
      hideLoader();
    }
  };

  // Dialog handlers
  const handleOpenUserDialog = async () => {
    try {
      showLoader('Checking user limits...');
      const response = await api.get(`account/new_user_allowed/`);
      
      // Check if response has data
      if (response?.data) {
        const { remaining, max_allowed } = response.data;
        setMaxAllowed(max_allowed);
        
        if (remaining > 0) {
          // Set default values based on user role
          const defaultUser = { 
            name: '', 
            email: '', 
            designation: '',
            mobile: '',
            role: ''
          };
          
          // If current user is MG, set default role
          if (user?.role === 'MG') {
            defaultUser.role = 'AG'; // Default to Agent for MG users
          }
          
          setNewUser(defaultUser);
          setOpenUserDialog(true);
        } else {
          setShowUserLimitModal(true);
        }
      } else {
        // Handle case where response doesn't have expected data
        openErrorModal(
          "Error",
          "Unable to verify user limits. Please try again."
        );
      }
    } catch (error) {
      console.log(error);
      // Handle API errors
      if (error.response?.data) {
        handleHttpError(error);
      } else {
        openErrorModal(
          "Error",
          "Unable to connect to server. Please check your connection."
        );
      }
    } finally {
      hideLoader();
    }
  };

  const handleCloseUserDialog = () => {
    setOpenUserDialog(false);
    // Reset to default values based on user role
    const defaultUser = { name: '', email: '', role: '', designation: '', mobile: '' };
    
    // If current user is MG, set default role
    if (user?.role === 'MG') {
      defaultUser.role = 'AG';
    }

    setNewUser(defaultUser);
    setFieldErrors({});
  };

  const handleAddUserSlot = () => {
    setShowUserLimitModal(false);
    setShowAddUserSlotModal(true);
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Only show tabs if Teams tab should be visible */}
      {showTeamsTab && (
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)}>
            <Tab label="Team Members" />
            <Tab label="Teams" />
          </Tabs>
        </Box>
      )}

      {/* Always show Team Members content */}
      {(tabValue === 0 || !showTeamsTab) && (
        <>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h5">Team Members</Typography>
            <Button
              variant="contained"
              startIcon={<PersonAddIcon />}
              onClick={handleOpenUserDialog}
            >
              Add Member
            </Button>
          </Box>
          <TeamMembers 
            users={users}
            teams={teams}
            userroles={userRoles}
            onEdit={handleEditUser}
            onRefresh={fetchUsersData}
            confirmUserDelete={confirmUserDelete}
            currentUser={user}
            whatsappSettings={whatsappSettings}
          />
        </>
      )}

      {/* Show Teams content only for admins */}
      {showTeamsTab && tabValue === 1 && (
        <>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h5">Teams</Typography>
          </Box>
          <Teams 
            teams={teams}
            users={users}
            onDelete={confirmTeamDelete}
            onEdit={handleEditTeam}
            onManageTeamMembers={handleManageTeamMembers}
            onRefresh={refreshAllData}
            onCreateTeam={handleCreateTeam}
          />
        </>
      )}

      {/* Add User Dialog */}
      <Dialog open={openUserDialog} onClose={handleCloseUserDialog} maxWidth="xs" fullWidth>
        <DialogTitle>Add Team Member</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Full Name"
              fullWidth
              required
              value={newUser.name}
              onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
              error={!!fieldErrors.name}
              helperText={fieldErrors.name}
            />
            <TextField
              label="Email Address"
              type="email"
              fullWidth
              required
              value={newUser.email}
              onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
              error={!!fieldErrors.email}
              helperText={fieldErrors.email}
            />
            <TextField
              label="Designation"
              type="text"
              fullWidth
              required
              value={newUser.designation}
              onChange={(e) => setNewUser({ ...newUser, designation: e.target.value })}
              error={!!fieldErrors.designation}
              helperText={fieldErrors.designation}
            />
            <TextField
              label="Mobile"
              type="text"
              inputProps={{
                pattern: '[0-9]*',
                inputMode: 'numeric'
              }}
              fullWidth
              required
              value={newUser.mobile}
              onChange={(e) => {
                if (e.target.value.length <= 10) {
                  setNewUser({ ...newUser, mobile: e.target.value });
                }
              }}
              error={!!fieldErrors.mobile}
              helperText={fieldErrors.mobile || ""}
            />
            <FormControl fullWidth required error={!!fieldErrors.role}>
              <InputLabel>Role</InputLabel>
              <Select
                value={newUser.role}
                label="Role"
                onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
                disabled={user?.role === 'MG'} // Disable role selection for MG users
              >
                {Array.isArray(userRoles) && userRoles.map((role) => (
                  <MenuItem 
                    key={role.value} 
                    value={role.value}
                    disabled={
                      (role.value === 'AD' && users.some(user => user.role === 'AD'))
                    }
                  >
                    {role.label}
                  </MenuItem>
                ))}
              </Select>
              {fieldErrors.role && (
                <FormHelperText error>{fieldErrors.role}</FormHelperText>
              )}
              {user?.role === 'MG' && (
                <FormHelperText>
                  Role is set to Agent. New members will be automatically assigned to your team.
                </FormHelperText>
              )}
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseUserDialog}>Cancel</Button>
          <Button 
            variant="contained" 
            onClick={handleAddUser}
            disabled={!newUser.name || !newUser.email || !newUser.role || !newUser.mobile}
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>

      <UserLimitAlertModal
        open={showUserLimitModal}
        onClose={() => setShowUserLimitModal(false)}
        onAddUserSlot={() => {
          setShowUserLimitModal(false);
          setShowQueryForm(true);
        }}
        maxAllowed={maxAllowed}
        currentPlan={currentPlan}
      />

      <TeamLimitAlertModal
        open={showTeamLimitModal}
        onClose={() => setShowTeamLimitModal(false)}
        maxAllowed={maxAllowed}
        currentPlan={currentPlan}
      />

      {showAddUserSlotModal && (
        <AddUserSlotModal
          open={showAddUserSlotModal}
          onClose={() => setShowAddUserSlotModal(false)}
          currentPlan={currentPlan}
        />
      )}

      {showQueryForm && (currentPlan.users?.additional_allowed ?? currentPlan.additional_slot_allowed) && (
        <QueryForm
          open={showQueryForm}
          onClose={() => setShowQueryForm(false)}
          queryType='AU'
        />
      )}
    </Box>
  );
};

export default UsersAndTeams;